import api from '../../utils/api';
import { getProductsAll, getProducts, getProductFilters } from './api/suppliers';
import { postPermissions } from './permissions';
import { alertError, alertSuccess } from './alert';
import { formatMany } from '../../utils/formatHelper';
import { TAB_DROPSHIP, TAB_REORDER } from '../../constants/mainConstants';
import { ORDER_TYPE_ECOMMERCE, ORDER_TYPE_REPLENISH } from '../../constants/orderTypes';

import { setProductFilters, setProductFiltersSetFilters } from './productFilters';

import {
  RETAILER_PRODUCTS,
  RETAILER_PRODUCTS_COUNT,
  RETAILER_PRODUCTS_SEARCH,
  RETAILER_PRODUCTS_ORDER,
  RETAILER_PRODUCTS_OFFSET,
  RETAILER_PRODUCTS_UNCHECK_ALL,
  RETAILER_PRODUCTS_CHECK,
  RETAILER_PRODUCTS_UNCHECK,
  RETAILER_PRODUCTS_APPROVE,
  RETAILER_PRODUCTS_UNAPPROVE,
  RETAILER_PRODUCTS_DETAILS,
  RETAILER_PRODUCTS_CHECK_ALL_ON_PAGE,
  RETAILER_PRODUCTS_ALL,
  RETAILER_PRODUCTS_APPROVED,
  RETAILER_PRODUCTS_MODAL_OPEN,
  RETAILER_PRODUCTS_MODAL_CLOSE,
  RETAILER_PRODUCTS_PRODUCT,
  RETAILER_PRODUCTS_LOADING,
  RETAILER_PRODUCTS_LOADING_PRODUCTS,
  RESET_ALL_FILTERS,
} from '../constants/retailerProductsTypes';
import { variantServiceProductEndpointEnabled, variantServiceProductFiltersEnabled } from '../../constants/featureFlag';
import { getTotalCountHeader } from './api/utils';

export const setRetailerProductsModalClose = { type: RETAILER_PRODUCTS_MODAL_CLOSE };

const setRetailerProductsLoading = (loadingProducts, value) => ({
  type: loadingProducts ? RETAILER_PRODUCTS_LOADING_PRODUCTS : RETAILER_PRODUCTS_LOADING,
  payload: value,
});

const setRetailerProducts = (data) => ({ type: RETAILER_PRODUCTS, payload: data });
const setRetailerProductsCount = (count) => ({ type: RETAILER_PRODUCTS_COUNT, payload: count });
const setProductDetails = (data) => ({ type: RETAILER_PRODUCTS_DETAILS, payload: data });
const setRetailerProductsAll = (data) => ({ type: RETAILER_PRODUCTS_ALL, payload: data });
const setRetailerProductsProduct = (data) => ({ type: RETAILER_PRODUCTS_PRODUCT, payload: data });
const resetFilters = (value) => ({ type: RESET_ALL_FILTERS, payload: value });

export const retailerProductsCheckAllOnPage = (ids) => ({ type: RETAILER_PRODUCTS_CHECK_ALL_ON_PAGE, payload: ids });

export const retailerProductsCheckAll = (retailerId) => async (dispatch, getState) => {
  const state = getState();
  const { search } = state.retailerProducts;
  const { supplierId } = state.role;
  const { tab } = state.permissions;
  const { currentFilters } = state.productFilters;

  try {
    const service = tab.toLowerCase() === TAB_DROPSHIP ? ORDER_TYPE_ECOMMERCE : ORDER_TYPE_REPLENISH;
    const responseAll = await getProductsAll(supplierId, currentFilters, search, service, retailerId);

    dispatch(setRetailerProductsAll(responseAll.data));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const retailerProductsUncheckAll = { type: RETAILER_PRODUCTS_UNCHECK_ALL };

export const retailerProductsCheck = (id) => async (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_CHECK, payload: id });
};

export const retailerProductsUncheck = (id) => async (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_UNCHECK, payload: id });
};

export const getRetailerProducts =
  (retailerId, productsLoading = true) =>
  async (dispatch, getState) => {
    const state = getState();
    const { perPage, offset, count, search, order, orderBy } = state.retailerProducts;
    const { supplierId } = state.role;
    const { tab } = state.permissions;
    const { currentFilters } = state.productFilters;
    const {
      flags: { [variantServiceProductEndpointEnabled]: variantServiceEnabled },
    } = state.featureFlags;

    try {
      dispatch(setRetailerProductsLoading(productsLoading, true));

      const service = tab.toLowerCase() === TAB_DROPSHIP ? ORDER_TYPE_ECOMMERCE : ORDER_TYPE_REPLENISH;
      const response = await getProducts(
        supplierId,
        currentFilters,
        offset,
        perPage,
        search,
        order,
        orderBy,
        service,
        retailerId || state.retailer.current?.retailer?.id,
        variantServiceEnabled,
      );

      const newCount = getTotalCountHeader(response);
      if (count !== newCount) {
        dispatch(setRetailerProductsCount(newCount));
      }

      dispatch(setRetailerProducts(response.data));
      dispatch(setRetailerProductsLoading(productsLoading, false));
    } catch (error) {
      dispatch(alertError(error));
      dispatch(setRetailerProductsLoading(productsLoading, false));
    }
  };

export const setRetailerProductsModalOpen = (product) => (dispatch) => {
  dispatch(setRetailerProductsProduct(product));
  dispatch(getRetailerProductsDetails(product.model));
  dispatch({ type: RETAILER_PRODUCTS_MODAL_OPEN });
};

export const getRetailerProductsDetails = (model) => async (dispatch, getState) => {
  try {
    dispatch(setProductDetails({}));
    const state = getState();
    const { supplierId } = state.role;
    const { id } = state.retailer.current.retailer;

    const response = await productsDetails(supplierId, model, id);

    dispatch(setProductDetails(response.data));
    dispatch(setRetailerProductsDefaultApproved());
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const setRetailerProductsDefaultApproved = () => (dispatch, getState) => {
  const state = getState();
  const { tab } = state.permissions;
  const {
    productDetails: { variants },
  } = state.retailerProducts;

  const dropship = tab.toLowerCase() === TAB_DROPSHIP;
  const replenishment = tab.toLowerCase() === TAB_REORDER;

  const approved = variants.reduce((approved, v) => {
    if (v.permissions?.length) {
      if (dropship && v.permissions[0].ecommerce_approved) {
        approved.push(v.id);
      } else if (replenishment && v.permissions[0].replenish_approved) {
        approved.push(v.id);
      }
    }
    return approved;
  }, []);

  dispatch({ type: RETAILER_PRODUCTS_APPROVED, payload: approved });
};

export const setRetailerProductsSearch = (search) => (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_SEARCH, payload: search });
  dispatch(getRetailerProducts());
};

export const setRetailerProductsOrder = (order, orderBy) => (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_ORDER, payload: { order, orderBy } });
  dispatch(getRetailerProducts());
};

export const setRetailerProductsOffset = (newOffset) => (dispatch, getState) => {
  const { offset } = getState().retailerProducts;
  if (newOffset !== offset) {
    dispatch({ type: RETAILER_PRODUCTS_OFFSET, payload: newOffset });
    dispatch(getRetailerProducts());
  }
};

export const getRetailerProductsFilters = (retailerId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { supplierId } = state.role;
    const { tab } = state.permissions;
    const service = tab.toLowerCase() === TAB_DROPSHIP ? ORDER_TYPE_ECOMMERCE : ORDER_TYPE_REPLENISH;
    const {
      flags: { [variantServiceProductFiltersEnabled]: variantServiceEnabled },
    } = state.featureFlags;
    const response = await getProductFilters(
      supplierId,
      service,
      retailerId || state.retailer.current?.retailer?.id,
      variantServiceEnabled,
    );

    dispatch(setProductFilters(response.data));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const setRetailerProductsCurrentFilter = (filters) => (dispatch) => {
  dispatch(setProductFiltersSetFilters(filters));
  dispatch(getRetailerProducts());
};

export const retailerProductsApprove = (variants) => (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_APPROVE, payload: variants });
};

export const retailerProductsUnapprove = (variants) => (dispatch) => {
  dispatch({ type: RETAILER_PRODUCTS_UNAPPROVE, payload: variants });
};

export const retailerProductsApproveChecked = (retailerId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { checked } = state.retailerProducts;
    const { tab } = state.permissions;
    const { supplierId } = state.role;

    const variants = [];

    Object.keys(checked).forEach((model) => {
      checked[model].forEach((variantId) => {
        variants.push(variantId.id || variantId);
      });
    });

    const data = {
      retailer_ids: [retailerId],
      variant_ids: variants,
    };

    if (tab.toLowerCase() === TAB_DROPSHIP) {
      data.ecommerce_approved = true;
    }

    if (tab.toLowerCase() === TAB_REORDER) {
      data.replenish_approved = true;
    }

    const response = await postPermissions(supplierId, [data]);

    if (response.data.success) {
      dispatch(alertSuccess(`${formatMany(variants.length, 'product')} successfully approved`));
      dispatch(resetFilters(true));
      dispatch(getRetailerProducts());
    } else {
      dispatch(alertError('Error'));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const retailerProductsUnapproveChecked = (retailerId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { checked } = state.retailerProducts;
    const { tab } = state.permissions;
    const { supplierId } = state.role;

    const variants = [];

    Object.keys(checked).forEach((model) => {
      checked[model].forEach((variantId) => {
        variants.push(variantId.id || variantId);
      });
    });

    const data = {
      retailer_ids: [retailerId],
      variant_ids: variants,
    };

    if (tab.toLowerCase() === TAB_DROPSHIP) {
      data.ecommerce_approved = false;
    }

    if (tab.toLowerCase() === TAB_REORDER) {
      data.replenish_approved = false;
    }

    const response = await postPermissions(supplierId, [data]);

    if (response.data.success) {
      dispatch(alertSuccess(`${formatMany(Object.keys(checked).length, 'product')} successfully revoked`));
      dispatch(resetFilters(true));
      dispatch(getRetailerProducts());
    } else {
      dispatch(alertError('Error'));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const retailerProductsSaveVariants = (retailerId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const { approved, unapproved } = state.retailerProducts;
    const { supplierId } = state.role;
    const { tab } = state.permissions;

    let approvedData = {
      retailer_ids: [retailerId],
      variant_ids: approved,
    };

    let unapprovedData = {
      retailer_ids: [retailerId],
      variant_ids: unapproved,
    };

    if (tab.toLowerCase() === TAB_DROPSHIP) {
      approvedData.ecommerce_approved = true;
      unapprovedData.ecommerce_approved = false;
    }

    if (tab.toLowerCase() === TAB_REORDER) {
      approvedData.replenish_approved = true;
      unapprovedData.replenish_approved = false;
    }

    const approveResponse = await postPermissions(supplierId, [approvedData, unapprovedData]);

    if (approveResponse.data.success) {
      dispatch(alertSuccess('Success'));
      dispatch(getRetailerProducts());
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

let cancelProductsDetails;
const productsDetails = (supplierId, model, retailerId) => {
  if (cancelProductsDetails) {
    cancelProductsDetails('Cancel product details');
  }

  model = encodeURIComponent(model);
  return api.get(`/suppliers/${supplierId}/products/${model}?permissions_for_retailer_id=${retailerId}`, {
    cancelToken: new api.CancelToken((c) => (cancelProductsDetails = c)),
  });
};
